import React, { useCallback, useEffect, useRef, useState } from 'react';
import type {
  TeamSecretActivityEvent,
  TeamSecretDetail as TeamSecretDetailModel,
  TeamSecretVersion,
} from '../client';
import { useApi } from '../state/ApiContext';
import AsyncButton from '../components/AsyncButton';
import Modal from '../components/Modal';
import SecretActivity from './SecretActivity';
import type { SecretActivityMember } from './SecretActivity';
import SecretEditor from './SecretEditor';
import SecretVersions from './SecretVersions';
import {
  GuardedActionButton,
  useGuardedCallback,
} from '../permissions/useGuardedAction';
import { SECRET_ACTION } from './secret-permissions';

export interface SecretAgent {
  id: number;
  name?: string;
}

interface SecretDetailProps {
  teamId: number;
  secretId: string;
  agents: SecretAgent[];
  members?: SecretActivityMember[];
  onDeleted?: () => void;
  onChanged?: (detail: TeamSecretDetailModel) => void;
}

type DetailTab = 'overview' | 'edit' | 'access' | 'versions' | 'activity';

const requestMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  fallback;

const SecretDetail: React.FC<SecretDetailProps> = ({
  teamId,
  secretId,
  agents,
  members = [],
  onDeleted,
  onChanged,
}) => {
  const { teamSecretsApi } = useApi();
  const [detail, setDetail] = useState<TeamSecretDetailModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState<DetailTab>('overview');
  const [rotating, setRotating] = useState(false);
  const [rotationValue, setRotationValue] = useState('');
  const [rotationError, setRotationError] = useState('');
  const [rotationPending, setRotationPending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletePending, setDeletePending] = useState(false);
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [grantPending, setGrantPending] = useState(false);
  const [activity, setActivity] = useState<TeamSecretActivityEvent[]>([]);
  const [activityCursor, setActivityCursor] = useState<string | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const onChangedRef = useRef(onChanged);

  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const applyDetail = useCallback((next: TeamSecretDetailModel) => {
    setDetail(next);
    setActivity(next.activity);
    setActivityCursor(next.activityNextCursor);
    onChangedRef.current?.(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await teamSecretsApi.getTeamSecret(teamId, secretId);
      applyDetail(response.data);
    } catch (requestError) {
      setError(requestMessage(requestError, 'Could not load this secret.'));
    } finally {
      setLoading(false);
    }
  }, [applyDetail, secretId, teamId, teamSecretsApi]);

  useEffect(() => {
    setDetail(null);
    setActivity([]);
    setActivityCursor(null);
    setTab('overview');
    setNotice('');
    void load();
  }, [load]);

  /**
   * Every write on this screen is `TEAM_SECRET: manage` (see
   * `secret-permissions.ts` for the route table), so these are eight verbs on
   * one requirement rather than eight different requirements.
   *
   * They are declared HERE, above the loading/error returns below, because
   * hooks cannot live after a conditional return. Each `onAllowed` closes over
   * a handler declared further down: the binding is only ever *called* from a
   * click, long after the whole body has run, so the reference is sound — and
   * on a render that returns early nothing is rendered to click.
   */
  const rotateGuard = useGuardedCallback<[React.FormEvent]>({
    ...SECRET_ACTION.ROTATE,
    testId: 'secret-rotate-denied',
    onAllowed: (event) => void rotate(event),
  });
  const deleteGuard = useGuardedCallback<[]>({
    ...SECRET_ACTION.DELETE,
    testId: 'secret-delete-denied',
    onAllowed: () => void deleteSecret(),
  });
  const restoreGuard = useGuardedCallback<[TeamSecretVersion]>({
    ...SECRET_ACTION.VERSION_RESTORE,
    testId: 'secret-version-restore-denied',
    onAllowed: (version) => void restore(version),
  });
  const versionDeleteGuard = useGuardedCallback<[TeamSecretVersion]>({
    ...SECRET_ACTION.VERSION_DELETE,
    testId: 'secret-version-delete-denied',
    onAllowed: (version) => void deleteVersion(version),
  });
  const grantGuard = useGuardedCallback<[number]>({
    ...SECRET_ACTION.GRANT,
    testId: 'secret-grant-denied',
    onAllowed: (agentId) => void changeGrant(agentId, true),
  });
  const ungrantGuard = useGuardedCallback<[number]>({
    ...SECRET_ACTION.UNGRANT,
    testId: 'secret-ungrant-denied',
    onAllowed: (agentId) => void changeGrant(agentId, false),
  });

  if (loading && (!detail || detail.id !== secretId)) {
    return <p className="secret-state">Loading secret ledger…</p>;
  }
  if (error && (!detail || detail.id !== secretId)) {
    return <p className="secret-error">{error}</p>;
  }
  if (!detail || detail.id !== secretId) return null;

  const rotate = async (event: React.FormEvent) => {
    event.preventDefault();
    setRotationPending(true);
    setRotationError('');
    try {
      const response = await teamSecretsApi.rotateTeamSecret(
        teamId,
        detail.id,
        {
          expectedCurrentVersionId: detail.currentVersionId,
          value: rotationValue,
        },
      );
      setRotationValue('');
      setRotating(false);
      applyDetail(response.data);
    } catch (requestError) {
      setRotationError(
        requestMessage(requestError, 'The secret could not be rotated.'),
      );
    } finally {
      setRotationPending(false);
    }
  };

  const restore = async (version: TeamSecretVersion) => {
    setPendingVersionId(version.id);
    setNotice('');
    try {
      const response = await teamSecretsApi.restoreTeamSecretVersion(
        teamId,
        detail.id,
        version.id,
        { expectedCurrentVersionId: detail.currentVersionId },
      );
      applyDetail(response.data);
      setNotice(
        `Version ${version.versionNumber} restored as a new current version.`,
      );
    } catch (requestError) {
      setNotice(
        requestMessage(requestError, 'The version could not be restored.'),
      );
      await load();
    } finally {
      setPendingVersionId(null);
    }
  };

  const deleteVersion = async (version: TeamSecretVersion) => {
    setPendingVersionId(version.id);
    setNotice('');
    try {
      const response = await teamSecretsApi.deleteTeamSecretVersion(
        teamId,
        detail.id,
        version.id,
      );
      applyDetail(response.data);
      setNotice(`Version ${version.versionNumber} permanently deleted.`);
    } catch (requestError) {
      setNotice(
        requestMessage(requestError, 'The version could not be deleted.'),
      );
      await load();
    } finally {
      setPendingVersionId(null);
    }
  };

  const deleteSecret = async () => {
    setDeletePending(true);
    setDeleteError('');
    try {
      await teamSecretsApi.deleteTeamSecret(teamId, detail.id, {
        confirmationName: deleteName,
        expectedRevision: detail.revision,
      });
      setDeleteName('');
      setDeleting(false);
      onDeleted?.();
    } catch (requestError: any) {
      setDeleteError(
        requestMessage(requestError, 'The secret could not be deleted.'),
      );
      if (requestError?.response?.status === 409) await load();
    } finally {
      setDeletePending(false);
    }
  };

  const changeGrant = async (agentId: number, grant: boolean) => {
    setGrantPending(true);
    setNotice('');
    try {
      const response = grant
        ? await teamSecretsApi.grantTeamSecret(teamId, detail.id, agentId)
        : await teamSecretsApi.ungrantTeamSecret(teamId, detail.id, agentId);
      applyDetail(response.data);
      setSelectedAgentId('');
    } catch (requestError) {
      setNotice(requestMessage(requestError, 'Access could not be changed.'));
      await load();
    } finally {
      setGrantPending(false);
    }
  };

  const loadMoreActivity = async () => {
    if (!activityCursor) return;
    setLoadingActivity(true);
    try {
      const response = await teamSecretsApi.listTeamSecretActivity(
        teamId,
        activityCursor,
        50,
      );
      setActivity((current) => [
        ...current,
        ...response.data.items.filter((event) => event.secretId === detail.id),
      ]);
      setActivityCursor(response.data.nextCursor);
    } catch (requestError) {
      setNotice(
        requestMessage(requestError, 'More activity could not be loaded.'),
      );
    } finally {
      setLoadingActivity(false);
    }
  };

  const grantedIds = new Set(detail.grants.map((grant) => grant.agentId));
  const availableAgents = agents.filter((agent) => !grantedIds.has(agent.id));

  return (
    <article className="secret-detail">
      <header className="secret-detail-header">
        <div>
          <span className="secret-eyebrow">Vault entry</span>
          <h3>
            <code>{detail.currentName}</code>
          </h3>
          <p>{detail.description}</p>
        </div>
      </header>

      <nav className="secret-tabs" role="tablist" aria-label="Secret details">
        {(
          ['overview', 'edit', 'access', 'versions', 'activity'] as DetailTab[]
        ).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'active' : ''}
            onClick={() => setTab(item)}>
            {item.replace(/^./, (first) => first.toUpperCase())}
          </button>
        ))}
      </nav>

      {notice && (
        <p className="secret-notice" role="status">
          {notice}
        </p>
      )}
      {error && <p className="secret-error">{error}</p>}

      <div className="secret-detail-body" role="tabpanel">
        {tab === 'overview' && (
          <section className="secret-overview">
            <dl className="secret-ledger-grid">
              <div>
                <dt>Current version</dt>
                <dd>
                  <code>v{detail.currentVersionNumber}</code>
                </dd>
              </div>
              <div>
                <dt>Assigned agents</dt>
                <dd>{detail.assignedAgentCount}</dd>
              </div>
              <div>
                <dt>Revision</dt>
                <dd>
                  <code>r{detail.revision}</code>
                </dd>
              </div>
              <div>
                <dt>Last updated</dt>
                <dd>{new Date(detail.updatedAt).toLocaleString()}</dd>
              </div>
            </dl>
            {!!detail.historicalNames.length && (
              <div className="secret-history-row">
                <span>Historical names</span>
                {detail.historicalNames.map((name) => (
                  <code key={name}>{name}</code>
                ))}
              </div>
            )}
            <div className="secret-retention-row">
              <div>
                <strong>Prior version retention</strong>
                <p>Applied to non-current versions of this secret.</p>
              </div>
              <strong>{detail.priorVersionRetentionDays} days</strong>
            </div>
            <section
              className="secret-policy-rail"
              aria-label="Destination policy">
              <div className="secret-policy-node">
                <span>Mode</span>
                <strong>Workspace</strong>
                <em>
                  {detail.policy.workspace.enabled ? 'Enabled' : 'Disabled'}
                </em>
              </div>
              <div className="secret-policy-track">
                {detail.policy.workspace.allowedHosts.map((host) => (
                  <code key={host}>{host}</code>
                ))}
              </div>
              <div className="secret-policy-state">
                <span>Assignment</span>
                <strong>
                  {detail.assignedAgentCount} agents · v
                  {detail.currentVersionNumber}
                </strong>
              </div>
              <div className="secret-policy-node">
                <span>Mode</span>
                <strong>Browser</strong>
                <em>
                  {detail.policy.browser.enabled ? 'Enabled' : 'Disabled'}
                </em>
              </div>
              <div className="secret-policy-track">
                {detail.policy.browser.allowedOrigins.map((origin) => (
                  <code key={origin}>{origin}</code>
                ))}
              </div>
              <div className="secret-policy-state">
                <span>Assignment</span>
                <strong>
                  {detail.assignedAgentCount} agents · v
                  {detail.currentVersionNumber}
                </strong>
              </div>
            </section>
          </section>
        )}

        {tab === 'edit' && (
          <section className="secret-edit-panel">
            <SecretEditor
              teamId={teamId}
              isOpen
              embedded
              secret={detail}
              onClose={() => {}}
              onSaved={(next) => {
                applyDetail(next);
                setNotice('Secret changes saved.');
              }}
              onConflict={async (message) => {
                await load();
                setNotice(
                  `${message} Latest changes were loaded; review them before retrying.`,
                );
              }}
            />
            <div className="secret-danger-zone">
              <h3 className="descriptionText secret-danger-zone-heading">
                Danger Zone
              </h3>
              <p className="descriptionText">
                <strong>Delete secret permanently.</strong> Removes all
                versions, assignments, requests, and name history.
              </p>
              {/* Guarded at the door as well as on the confirm button: being
                  refused after typing the secret's name to confirm is a worse
                  way to learn you cannot delete it. */}
              <GuardedActionButton
                {...SECRET_ACTION.DELETE}
                testId="secret-delete-open-denied"
                className="delete-button"
                data-testid="secret-delete-open"
                onAllowed={() => setDeleting(true)}>
                Delete
              </GuardedActionButton>
            </div>
          </section>
        )}

        {tab === 'access' && (
          <section className="secret-access-list">
            <div className="secret-grant-control">
              <label htmlFor="secret-agent-grant">Grant an agent access</label>
              <select
                id="secret-agent-grant"
                value={selectedAgentId}
                disabled={grantPending || !availableAgents.length}
                onChange={(event) => setSelectedAgentId(event.target.value)}>
                <option value="">Select an agent</option>
                {availableAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name || `Agent #${agent.id}`}
                  </option>
                ))}
              </select>
              <AsyncButton
                type="button"
                disabled={!selectedAgentId || grantPending}
                loading={grantPending}
                loadingText="Granting…"
                onClick={() => grantGuard.run(Number(selectedAgentId))}>
                Grant access
              </AsyncButton>
            </div>
            {detail.grants.length ? (
              detail.grants.map((grant) => {
                const agent = agents.find((item) => item.id === grant.agentId);
                return (
                  <div className="secret-access-row" key={grant.id}>
                    <div>
                      <strong>
                        {agent?.name || `Agent #${grant.agentId}`}
                      </strong>
                      <span>
                        Granted {new Date(grant.grantedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <AsyncButton
                      type="button"
                      className="lightButton"
                      disabled={grantPending}
                      loading={grantPending}
                      loadingText="Removing…"
                      onClick={() => ungrantGuard.run(grant.agentId)}>
                      Remove access
                    </AsyncButton>
                  </div>
                );
              })
            ) : (
              <p className="secret-state">No agents are assigned.</p>
            )}
          </section>
        )}

        {tab === 'versions' && (
          <section className="secret-versions-panel">
            <div className="secret-section-actions">
              <div>
                <strong>Secret versions</strong>
                <p>Create a new write-only value or restore a prior version.</p>
              </div>
              <GuardedActionButton
                {...SECRET_ACTION.ROTATE}
                testId="secret-rotate-open-denied"
                data-testid="secret-rotate-open"
                onAllowed={() => setRotating(true)}>
                Rotate
              </GuardedActionButton>
            </div>
            <SecretVersions
              versions={detail.versions}
              pendingVersionId={pendingVersionId}
              onRestore={(version) => restoreGuard.run(version)}
              onDelete={(version) => versionDeleteGuard.run(version)}
            />
          </section>
        )}

        {tab === 'activity' && (
          <SecretActivity
            teamId={String(teamId)}
            events={activity}
            members={members}
            agents={agents}
            nextCursor={activityCursor}
            loadingMore={loadingActivity}
            onLoadMore={() => void loadMoreActivity()}
          />
        )}
      </div>

      <Modal
        isOpen={rotating}
        onClose={() => {
          setRotationValue('');
          setRotationError('');
          setRotating(false);
        }}
        title="Rotate secret"
        isBusy={rotationPending}>
        <form
          className="secret-editor"
          onSubmit={(event) => {
            // Stop the submit first: a denied Enter must not fall through to a
            // native form post while the explanation opens.
            event.preventDefault();
            rotateGuard.run(event);
          }}>
          <p className="descriptionText">
            Rotation creates a new current version. The existing value is never
            loaded.
          </p>
          <label>
            New secret value
            <input
              type="password"
              autoComplete="new-password"
              required
              value={rotationValue}
              onChange={(event) => setRotationValue(event.target.value)}
            />
          </label>
          {rotationError && <p className="secret-error">{rotationError}</p>}
          <div className="secret-modal-actions">
            <button
              type="button"
              className="lightButton"
              disabled={rotationPending}
              onClick={() => {
                setRotationValue('');
                setRotating(false);
              }}>
              Cancel
            </button>
            <AsyncButton
              type="submit"
              loading={rotationPending}
              loadingText="Rotating…">
              Rotate secret
            </AsyncButton>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={deleting}
        onClose={() => {
          setDeleteName('');
          setDeleteError('');
          setDeleting(false);
        }}
        title="Delete secret permanently"
        isBusy={deletePending}>
        <div className="secret-delete-copy">
          <p>
            This removes current and prior versions, grants, pending requests,
            and name history. Active storage is deleted immediately; platform
            backups follow their own retention schedules.
          </p>
          <label htmlFor="secret-delete-name">
            Type {detail.currentName} to confirm
          </label>
          <input
            id="secret-delete-name"
            type="text"
            value={deleteName}
            onChange={(event) => setDeleteName(event.target.value)}
          />
          {deleteError && <p className="secret-error">{deleteError}</p>}
          <div className="secret-modal-actions">
            <button
              type="button"
              className="lightButton"
              disabled={deletePending}
              onClick={() => {
                setDeleteName('');
                setDeleting(false);
              }}>
              Cancel
            </button>
            <AsyncButton
              className="delete-button"
              loading={deletePending}
              loadingText="Deleting…"
              disabled={deleteName !== detail.currentName}
              onClick={() => deleteGuard.run()}>
              Delete permanently
            </AsyncButton>
          </div>
        </div>
      </Modal>

      {/* Every guard's explanation. Rendered once, here, rather than beside
          each control: the tabs above unmount their panels, and a modal that
          unmounts with the control that opened it never gets read. */}
      {rotateGuard.denialModal}
      {deleteGuard.denialModal}
      {restoreGuard.denialModal}
      {versionDeleteGuard.denialModal}
      {grantGuard.denialModal}
      {ungrantGuard.denialModal}
    </article>
  );
};

export default SecretDetail;
